import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { ListBulletsIcon } from "@phosphor-icons/react/dist/csr/ListBullets";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@volca/ui/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import type { ComparisonSheetSummary } from "@/payroll/editor/types";

/**
 * Every sheet in one list, the way the payroll grid offers them.
 *
 * The strip beside it holds the sheets you are moving between; this holds all
 * of them. A real workbook here runs to 376, so the strip is a window onto a
 * list far longer than it, and scrolling sideways through hundreds of names to
 * reach one you can already name is not a way to find anything.
 *
 * It leads the strip rather than following it, so the control that opens the
 * whole list sits where the list starts.
 */
export const SheetSwitcher = ({
  sheets,
  activeIndex,
  onSelect,
}: {
  sheets: ComparisonSheetSummary[];
  activeIndex: number | null;
  onSelect: (index: number) => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="All sheets"
                  className="text-subtle"
                />
              }
            />
          }
        >
          <ListBulletsIcon className="h-4 w-4" />
        </TooltipTrigger>
        <TooltipContent>All sheets</TooltipContent>
      </Tooltip>
      <PopoverContent align="start" side="bottom" className="w-80 p-0">
        <Command>
          <CommandInput placeholder="Find a sheet..." />
          <CommandList>
            <CommandEmpty>No sheets found.</CommandEmpty>
            <CommandGroup heading="Sheets">
              {sheets.map((sheet) => (
                <CommandItem
                  key={sheet.index}
                  // The index goes in the value so two sheets that share a name
                  // are still two rows the filter can tell apart.
                  value={`${sheet.name} ${sheet.index}`}
                  onSelect={() => {
                    onSelect(sheet.index);
                    setOpen(false);
                  }}
                  className="min-w-0"
                >
                  <span className="min-w-0 flex-1 truncate" title={sheet.name}>
                    {sheet.name}
                  </span>
                  {/* The same mark the strip carries, so a sheet that changed
                      looks the same in both places. */}
                  {sheet.state !== "unchanged" && (
                    <span
                      aria-label={`${sheet.changed_count} changed`}
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent-teal-bold"
                    />
                  )}
                  <CheckIcon
                    aria-hidden
                    className={cn("h-4 w-4 shrink-0", {
                      "opacity-100": sheet.index === activeIndex,
                      "opacity-0": sheet.index !== activeIndex,
                    })}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
